import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import { useAppStore } from "@/store/app/slice";
import type { IInspector } from "@/types/users-inspectors";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchInspectorProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<IInspector | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const inspectorsRepository = new InspectorsRepository(
      new InspectorsService(),
    );

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    const handleError = serverErrorHandler();

    Promise.all([
      inspectorsRepository.getProfileByUserId({ userId: user.id }, { signal }),
      Promise.allSettled([
        inspectorsRepository.getW9Details({ signal }),
        inspectorsRepository.getLicenseDetails({ signal }),
        inspectorsRepository.getHudDetails({ signal }),
      ]),
    ])
      .then(([inspector, [w9Form, license, hudDetail]]) => {
        [w9Form, license, hudDetail].forEach((result) => {
          if (result.status === "rejected") handleError(result.reason);
        });

        setProfile({
          ...inspector,
          w9Form: w9Form.status === "fulfilled" ? w9Form.value : undefined,
          license:
            license.status === "fulfilled" ? license.value : inspector.license,
          hudDetail:
            hudDetail.status === "fulfilled"
              ? hudDetail.value
              : inspector.hudDetail,
        });
      })
      .catch(handleError);
  }, [user]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
